import React, { useState, useEffect } from 'react';
import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';
import AIDataForm from './MashoAI/AIDataForm';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  DocumentIcon, 
  CubeIcon, 
  UserIcon, 
  UserGroupIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, loading, entryData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="ml-3 w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this AI training data entry? This action cannot be undone.
            </p>
            
            {entryData && (
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <p className="text-sm font-medium text-gray-700">Entry Details:</p>
                <p className="text-sm text-gray-600">Product: {entryData.product_id}</p>
                <p className="text-sm text-gray-600">Name: {entryData.product_name}</p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Entry'
                )}
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminAiPage = () => {
  const [activeTab, setActiveTab] = useState('ai-data');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiEntries, setAiEntries] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    entryToDelete: null
  });

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API || 'http://127.0.0.1:8000';

  // Fetch products and AI data on load
  useEffect(() => {
    fetchProducts();
    fetchAiData();
  }, []);

  // --- 1. Fetch Products Integration ---
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/list-products`);
      if (response.ok) {
        const data = await response.json();
        // The API returns { products: [...] }
        setProducts(data.products || []);
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // --- 2. Fetch AI Data Integration ---
  const fetchAiData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/masho-ai/list-data/`);
      if (response.ok) {
        const data = await response.json();
        setAiEntries(data);
      } else {
        throw new Error('Failed to fetch AI data list');
      }
    } catch (error) {
      console.error('Error fetching AI data:', error);
      toast.error('Could not load AI entries');
    }
  };

  // --- 3. Submit AI Data Integration ---
  const handleAiDataSubmit = async (formData) => {
    try {
      // Validate that product_id is required, others are optional
      if (!formData.product_id || formData.product_id.trim() === '') {
        toast.error('Product ID is required');
        return;
      }

      // Clean up optional fields - remove empty arrays and ensure at least empty arrays for backend
      const cleanedData = {
        product_id: formData.product_id.trim(),
        occasion: Array.isArray(formData.occasion) ? formData.occasion.filter(item => item && item.trim()) : [],
        situation: Array.isArray(formData.situation) ? formData.situation.filter(item => item && item.trim()) : [],
        programs: Array.isArray(formData.programs) ? formData.programs.filter(item => item && item.trim()) : [],
        gender: Array.isArray(formData.gender) ? formData.gender.filter(item => item && item.trim()) : []
      };

      // Your backend expects the data to be inside a stringified 'ai_data' key
      const payload = {
        ai_data: JSON.stringify(cleanedData)
      };

      const response = await fetch(`${API_BASE_URL}/masho-ai/add-data/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        // Add the returned new entry to local state (avoids re-fetching)
        const newEntry = {
          ...result, 
          // Ensure these fields exist if backend response doesn't strictly match UI needs yet
          id: result.id, 
          product_name: result.product_name || products.find(p => p.id === cleanedData.product_id)?.name
        };
        
        setAiEntries(prev => [newEntry, ...prev]);
        toast.success('AI data saved successfully!');
        
        // Optional: Switch tab to view data
        // setActiveTab('view-data'); 
      } else {
        throw new Error(result.error || 'Failed to save AI data');
      }
    } catch (error) {
      console.error('Error saving AI data:', error);
      toast.error(error.message || 'Failed to save AI data');
    }
  };

  // --- 4. Delete Modal Functions ---
  const openDeleteModal = (entry) => {
    setDeleteModal({
      isOpen: true,
      entryToDelete: entry
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      entryToDelete: null
    });
    setDeleteLoading(false);
  };

  const confirmDelete = async () => {
    if (!deleteModal.entryToDelete) return;

    setDeleteLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/masho-ai/delete-data/${deleteModal.entryToDelete.id}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (response.ok) {
        // Remove entry from local state
        setAiEntries(prev => prev.filter(entry => entry.id !== deleteModal.entryToDelete.id));
        toast.success(result.message || 'Entry deleted successfully!');
        closeDeleteModal();
      } else {
        throw new Error(result.error || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.message || 'Failed to delete entry');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Helper function to render array items with fallback
  const renderArrayItems = (items, bgColor, textColor) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <span className="text-xs text-gray-400 italic">Not specified</span>;
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item, idx) => (
          <span key={idx} className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${bgColor} ${textColor}`}>
            {item}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="fixed top-0 left-0 right-0 z-30">
        <AdminNavbar />
      </div>
      
      <div className="flex flex-1 pt-16">
        <div className="fixed top-16 left-0 bottom-0 z-20">
          <AdminSidebar activePage="ai" />
        </div>
        
        <main className="flex-1 ml-72 p-6">
          <ToastContainer position="top-right" autoClose={3000} />
          
          {/* Delete Confirmation Modal */}
          <DeleteConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={closeDeleteModal}
            onConfirm={confirmDelete}
            loading={deleteLoading}
            entryData={deleteModal.entryToDelete}
          />
          
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Masho AI Database</h1>
            <p className="text-gray-600 mt-1">
              Add and manage AI training data for personalized fragrance recommendations
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6 bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('ai-data')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'ai-data'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Add AI Data
                </button>
                <button
                  onClick={() => setActiveTab('view-data')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'view-data'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  View AI Entries ({aiEntries.length})
                </button>
              </nav>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'ai-data' ? (
            <div>
              {loading ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading products...</p>
                </div>
              ) : (
                <>
                  <div className="mb-6 p-6 bg-white rounded-lg shadow">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Add New AI Training Data</h2>
                    <p className="text-gray-600 mb-3">
                      Fill in the details below to add training data for the AI recommendation system.
                      Each entry helps improve personalized fragrance suggestions.
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            <strong>Note:</strong> Only Product ID is required. All other fields (Occasion, Situation, Programs, Gender) are optional and can be left empty.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Pass fetched products to form */}
                  <AIDataForm 
                    onSubmit={handleAiDataSubmit} 
                    products={products} 
                  />
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">AI Training Data Entries</h2>
                    <p className="text-gray-600 text-sm mt-1">
                      View all saved AI training data entries
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('ai-data')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add New Entry
                  </button>
                </div>
              </div>

              {aiEntries.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No AI Data Entries</h3>
                  <p className="text-gray-500 mb-4">Start by adding some AI training data using the "Add AI Data" tab.</p>
                  <button
                    onClick={() => setActiveTab('ai-data')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Add First Entry
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Occasion
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Situation
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Programs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gender
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {aiEntries.map((entry) => (
                        <tr key={entry.id || entry._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{entry.product_id}</div>
                              <div className="text-sm text-gray-500">{entry.product_name || 'Unknown Product'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {renderArrayItems(entry.occasion, 'bg-blue-100', 'text-blue-800')}
                          </td>
                          <td className="px-6 py-4">
                            {renderArrayItems(entry.situation, 'bg-green-100', 'text-green-800')}
                          </td>
                          <td className="px-6 py-4">
                            {renderArrayItems(entry.programs, 'bg-yellow-100', 'text-yellow-800')}
                          </td>
                          <td className="px-6 py-4">
                            {renderArrayItems(entry.gender, 'bg-purple-100', 'text-purple-800')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => openDeleteModal(entry)}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Stats Panel */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <DocumentIcon className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Entries</p>
                  <p className="text-2xl font-semibold text-gray-900">{aiEntries.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <CubeIcon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Unique Products</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {[...new Set(aiEntries.map(entry => entry.product_id))].length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <UserIcon className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">For Male</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {aiEntries.filter(entry => entry.gender?.includes('male')).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-pink-100 rounded-lg">
                    <UserGroupIcon className="w-6 h-6 text-pink-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">For Female</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {aiEntries.filter(entry => entry.gender?.includes('female')).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminAiPage;